import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  ChangeEvent,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import { produce } from "immer";
import { ChevronDownIcon, XIcon, ScanSearch } from "lucide-react";
import { createPortal } from "react-dom";

import {
  useSearchAnythingQuery,
  useGetAvsTagsNotLoginQuery,
} from "@/features/auth/api/authAPI";
import { getFilterState, filterActions } from "@/entities/filter";
import { getFilterAppsState } from "@/entities/filterApps";
import { useCurrentOrganization, useMe, useAPI } from "@/Hooks";
import { loadingStatesActions } from "@/entities/loadingStates";
import mixpanel from "@/shared/lib/mixpanel";
import { handleOpenVideoPage } from "@/app/libs/const/handleOpenVideoPage";
import { useWindowWidth } from "@/Hooks/useWindowWidth";
import { useResetFilters } from "@/Hooks/useResetFilters";

import MagicOCR from "@/widgets/magicOCR/ui/MagicOCR/MagicOCR";
import ImageSearchModalContent from "./ImageSearch/ImageSearchModal/ImageSearchModalContent";
import ExploreAppsStyles from "@/Views/Virtual/ExplorAppsNew.module.scss";

import { SEARCH_MODE_OCR, SEARCH_MODE_VECTOR } from "@/Views/constants";

const MAX_TAGS_TO_SHOW = 7;

interface SearchModalProps {
  searchValue: string;
  setSearchValue: (value: string) => void;
  searchInputRef: React.RefObject<HTMLInputElement>;
  isQuerySuccessful: boolean;
  setIsQuerySuccessful: (value: boolean) => void;
}

const SearchModal: React.FC<SearchModalProps> = ({
  searchValue,
  setSearchValue,
  searchInputRef,
  isQuerySuccessful,
  setIsQuerySuccessful,
}) => {
  const dispatch = useDispatch();
  const api = useAPI();
  const navigate = useNavigate();
  const location = useLocation();
  const windowWidth = useWindowWidth();
  const { me } = useMe();
  const currentOrganization = useCurrentOrganization();

  // Refs
  const modalRef = useRef<HTMLDivElement>(null);
  const magicOcrRef = useRef<HTMLDivElement>(null);
  const { ref: anythingSearchRef, inView: anythingSearchInView } = useInView({
    threshold: 0.5,
  });

  // State
  const [anythingsearchOffset, setAnythingsearchOffset] = useState(0);
  const [anythingsearchLimit, setAnythingsearchLimit] = useState(20);
  const [anythingSearchResult, setAnythingSearchResult] = useState([]);
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [screenTypes, setScreenTypes] = useState([]);
  const [uielements, setUielements] = useState([]);
  const [activeSearchIndex, setActiveSearchIndex] = useState(0);
  const [shouldShowSpinner, setShouldShowSpinner] = useState(false);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  const [hasReachedEnd, setHasReachedEnd] = useState(false);
  const [isManualTyping, setIsManualTyping] = useState(true);
  const [showMoreStates, setShowMoreStates] = useState({
    trending: false,
    screenTypes: false,
    uiElements: false,
  });

  // Redux state
  const { isActiveMagic, isOcrSelected } = useSelector(getFilterState);
  const { data: notLoginTags = [] } = useGetAvsTagsNotLoginQuery();
  const { resetAllFilters } = useResetFilters();

  const isUserPro = me?.is_pro || currentOrganization?.is_pro;

  // For non-pro users, ignore search value and always show default content
  const effectiveSearchValue = isUserPro ? searchValue : "";
  const effectiveDebouncedSearchValue = isUserPro ? debouncedSearchValue : "";

  // Reset search results when search index or search value changes
  useEffect(() => {
    setAnythingSearchResult([]);
    setAnythingsearchOffset(0);
    setHasReachedEnd(false);
  }, [activeSearchIndex, effectiveDebouncedSearchValue.length]);

  // Fetch screen types and UI elements
  const fetchScreenTypes = () => {
    api.get("/v1/tags/screen-types/").then((res: any) => {
      setScreenTypes(res.data);
    });
  };

  const fetchUielements = () => {
    api.get("/v1/tags/ui-elements/").then((res: any) => {
      setUielements(res.data);
    });
  };

  useEffect(() => {
    fetchScreenTypes();
    fetchUielements();
  }, []);

  // Button search data
  const buttonSearchData = [
    {
      icon: "/img/blackIcon/Search.svg",
      name: "Trending Searches",
    },
    {
      icon: "/img/blackIcon/Screen.svg",
      name: "Apps",
    },
    {
      icon: "/img/blackIcon/Developer.svg",
      name: "Developers",
    },
    {
      icon: "/img/blackIcon/Screen2.svg",
      name: "Screen Types",
    },
    {
      icon: "/img/blackIcon/UI.svg",
      name: "UI Elements",
    },
    {
      icon: <ScanSearch />,
      name: "Image Search",
    },
  ];

  // Search configuration
  const searchIndex =
    activeSearchIndex == 1 && effectiveDebouncedSearchValue.length == 0
      ? "apps"
      : activeSearchIndex == 2 && effectiveDebouncedSearchValue.length == 0
        ? "developers"
        : "all";
  const searchSort = !effectiveSearchValue ? "asc" : undefined;

  // Search query - only make API call for pro users
  const { data: anythingSearchResults, isFetching: isAnythingSearchLoading } =
    useSearchAnythingQuery(
      {
        q: effectiveDebouncedSearchValue,
        i: searchIndex,
        s: searchSort,
        limit: anythingsearchLimit,
        offset: anythingsearchOffset,
      },
      {
        refetchOnMountOrArgChange: true,
      },
    );

  // Update loading state
  useEffect(() => {
    dispatch(
      loadingStatesActions.setAnythingSearchLoading(isAnythingSearchLoading),
    );
  }, [isAnythingSearchLoading, dispatch]);

  // Handle infinite scroll
  useEffect(() => {
    if (
      anythingSearchInView &&
      !isAnythingSearchLoading &&
      activeSearchIndex !== 0 &&
      activeSearchIndex !== 3 &&
      activeSearchIndex !== 4 &&
      activeSearchIndex !== 5 &&
      !hasReachedEnd
    ) {
      setAnythingsearchOffset(anythingsearchOffset + anythingsearchLimit);
    }
  }, [
    anythingSearchInView,
    isAnythingSearchLoading,
    activeSearchIndex,
    hasReachedEnd,
  ]);

  // Handle debounced search value
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(effectiveSearchValue);
    }, 300);

    return () => clearTimeout(timer);
  }, [effectiveSearchValue]);

  // Handle search results
  useEffect(() => {
    if (anythingSearchResults && shouldShowSpinner) {
      setShouldShowSpinner(false);
      setHasInitialLoad(true);
    }
  }, [anythingSearchResults]);

  useEffect(() => {
    if (anythingSearchResults) {
      if ((anythingSearchResults as any).hits?.length === 0) {
        setHasReachedEnd(true);
      }

      setAnythingSearchResult((prevResults: any) => {
        if (!hasInitialLoad) {
          return [...(anythingSearchResults as any).hits];
        }
        return produce(prevResults, (draft) => {
          draft.push(...(anythingSearchResults as any).hits);
        });
      });
    }
  }, [anythingSearchResults, hasInitialLoad]);

  // Event handlers
  const handleChangeOCR = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchValue(newValue);
    setIsQuerySuccessful(false);
    setIsManualTyping(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch(false);
    }
  };

  const handleSearch = useCallback(
    async (isOcr: boolean) => {
      if (!searchValue.trim()) {
        toast.warn("Please enter a search term");
        return;
      }

      dispatch(filterActions.setOcrSelected(isOcr));

      const params = new URLSearchParams({
        q: searchValue,
        smode: isOcr ? SEARCH_MODE_OCR : SEARCH_MODE_VECTOR,
      });

      dispatch(filterActions.deactivateFilterMagic());

      mixpanel.track("Search params", {
        search_term: searchValue,
        search_mode: isOcr ? "text" : "elements",
        UserEmail: me?.email || "",
        OrganizationID: currentOrganization?.uuid || "",
      });

      if (me?.email) {
        mixpanel.identify(me.email);
        setTimeout(() => {
          mixpanel.people.increment("total_performed_searches", 1);
          mixpanel.people.union({
            history_of_searches: [searchValue],
          });
        }, 100);
      }

      const searchPath = "/search/";
      navigate({ pathname: searchPath, search: params.toString() });
    },
    [searchValue, navigate, dispatch, me, currentOrganization],
  );

  const handleFastSearchClick = (text: string) => {
    setSearchValue(text);
    setIsQuerySuccessful(false);
    setIsManualTyping(false);

    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }

    const params = new URLSearchParams({
      q: text,
      smode: isOcrSelected ? SEARCH_MODE_OCR : SEARCH_MODE_VECTOR,
    });

    dispatch(filterActions.deactivateFilterMagic());
    resetAllFilters();

    const searchPath = "/search/";
    navigate({
      pathname: searchPath,
      search: params.toString(),
    });
  };

  const handleFastSearchClickNoLogin = (text: string) => {
    setSearchValue(text);
    setIsQuerySuccessful(false);
    setIsManualTyping(false);

    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }

    const params = new URLSearchParams({
      q: text,
      smode: isOcrSelected ? SEARCH_MODE_OCR : SEARCH_MODE_VECTOR,
    });

    resetAllFilters();
    dispatch(filterActions.deactivateFilterMagic());

    if (me?.email) {
      mixpanel.identify(me.email);
      setTimeout(() => {
        mixpanel.people.increment("total_performed_searches", 1);
        mixpanel.people.union({
          history_of_searches: [text],
        });
      }, 100);
    }
    mixpanel.track("Search params", {
      search_term: searchValue,
      search_mode: isOcrSelected ? "text" : "elements",
      UserEmail: me?.email || "",
      OrganizationID: currentOrganization?.uuid || "",
    });

    const searchPath = "/search/";
    navigate({
      pathname: searchPath,
      search: params.toString(),
    });
  };

  const handleSearchButtonClick = (index: number) => {
    if (activeSearchIndex !== index) {
      setActiveSearchIndex(index);
      if (index === 1 || index === 2) {
        setShouldShowSpinner(true);
        setHasInitialLoad(false);
      }
      setAnythingSearchResult([]);

      const contentContainer = document.querySelector(
        `.${ExploreAppsStyles.containerSearchContent}`,
      );
      if (contentContainer) {
        contentContainer.scrollTop = 0;
      }
    }
  };

  const toggleShowMore = (section: keyof typeof showMoreStates) => {
    setShowMoreStates((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const checkMagicOcrClick = () => {
    mixpanel.track("Search modal", {
      UserEmail: me?.email || "",
      OrganizationID: currentOrganization?.uuid || "",
    });
  };

  // Handle click outside modal to close it
  useEffect(() => {
    const handleClickOutput = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node) &&
        magicOcrRef.current &&
        !magicOcrRef.current.contains(event.target as Node)
      ) {
        const isInteractiveElement = (event.target as HTMLElement).closest(
          "img,button,input,textarea,select,a, .magicOcrContentActiveMenuNew .magicMenuItemNew .menuGroupNew",
        );
        const isWithinSearchResults = (event.target as HTMLElement).closest(
          ".containerMagicMenuFast, .magicOcrContentActiveMenuNew",
        );

        if (!isInteractiveElement && !isWithinSearchResults) {
          dispatch(filterActions.deactivateFilterMagic());
          setSearchValue("");
          setIsQuerySuccessful(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutput);

    return () => {
      document.removeEventListener("mousedown", handleClickOutput);
    };
  }, [dispatch, searchValue, setSearchValue, setIsQuerySuccessful]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isActiveMagic) {
        dispatch(filterActions.deactivateFilterMagic());
        setSearchValue("");
        setIsQuerySuccessful(false);
      }
    };

    document.addEventListener("keydown", handleEscKey);

    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [dispatch, isActiveMagic, setSearchValue, setIsQuerySuccessful]);

  // Prevent scrolling and pointer events when modal is open
  useEffect(() => {
    if (isActiveMagic && !isQuerySuccessful) {
      // Disable scrolling
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      // Re-enable scrolling
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }

    // Cleanup function
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [isActiveMagic, isQuerySuccessful]);

  // Create overlay component
  const overlay = isActiveMagic && !isQuerySuccessful && (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        zIndex: 6,
        pointerEvents: "auto", // This blocks background interactions
      }}
    />
  );

  return (
    <>
      {/* Portal for overlay */}
      {typeof document !== "undefined" &&
        overlay &&
        createPortal(overlay, document.body)}

      <div className={ExploreAppsStyles.headerSearch}>
        {isActiveMagic && !isQuerySuccessful && (
          <AnimatePresence mode="wait">
            <motion.div
              className={ExploreAppsStyles.containerMagicOcr}
              style={{ pointerEvents: "auto" }} // Allow interactions within modal
              variants={{
                hidden: {
                  opacity: 0,
                  x: "-50%",
                  y: 0,
                  scale: 0.8,
                },
                visible: {
                  opacity: 1,
                  x: "-50%",
                  y: 0,
                  scale: 1,
                  transition: {
                    type: "spring",
                    duration: 0.4,
                    bounce: 0.2,
                  },
                },
                exit: {
                  opacity: 0,
                  x: "-50%",
                  y: "-100%",
                  scale: 0.8,
                  transition: {
                    type: "spring",
                    duration: 0.3,
                    bounce: 0,
                  },
                },
              }}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <div
                ref={magicOcrRef}
                className={ExploreAppsStyles.MagicOcrWrapper}
              >
                <div
                  onClick={checkMagicOcrClick}
                  ref={modalRef}
                  className={`${ExploreAppsStyles.MagicOcr} `}
                >
                  <MagicOCR
                    searchValue={searchValue}
                    handleChangeOCR={handleChangeOCR}
                    onKeyDown={handleKeyDown}
                    style={{ width: "100%" }}
                    onSearch={handleSearch}
                    ref={searchInputRef}
                  />
                  <div
                    className={ExploreAppsStyles.closeIcon}
                    onClick={() =>
                      dispatch(filterActions.deactivateFilterMagic())
                    }
                  >
                    <XIcon size={20} color="#AAA" />
                  </div>
                </div>

                <div
                  className={ExploreAppsStyles.containerSearchList}
                  style={{
                    height:
                      windowWidth < 640
                        ? "calc(100% - 40px)"
                        : activeSearchIndex === 1 ||
                            effectiveSearchValue.length > 0
                          ? "75vh"
                          : "500px",
                  }}
                >
                  {effectiveSearchValue && (
                    <>
                      <div
                        className={ExploreAppsStyles.searchResultItem}
                        onClick={() => handleSearch(false)}
                      >
                        <div className={ExploreAppsStyles.searchResultItemIcon}>
                          <img
                            src="/img/blackIcon/SparkRight.svg"
                            alt={effectiveSearchValue}
                            className={ExploreAppsStyles.searchResultItemImg}
                          />
                        </div>
                        <div className={ExploreAppsStyles.resultContainer}>
                          <div
                            title={effectiveSearchValue}
                            className={ExploreAppsStyles.resultName}
                          >
                            {effectiveSearchValue}
                          </div>
                          <div className={ExploreAppsStyles.resultCategory}>
                            AI Search
                          </div>
                        </div>
                      </div>

                      <div
                        className={ExploreAppsStyles.searchResultItem}
                        onClick={() => handleSearch(true)}
                      >
                        <div className={ExploreAppsStyles.searchResultItemIcon}>
                          <img
                            src="/img/blackIcon/TextInscreen.svg"
                            alt={effectiveSearchValue}
                            className={ExploreAppsStyles.searchResultItemImg}
                          />
                        </div>
                        <div className={ExploreAppsStyles.resultContainer}>
                          <div
                            title={effectiveSearchValue}
                            className={ExploreAppsStyles.resultName}
                          >
                            {effectiveSearchValue}
                          </div>
                          <div className={ExploreAppsStyles.resultCategory}>
                            Text inside Screens
                          </div>
                        </div>
                      </div>

                      {anythingSearchResult.map((item: any, index: number) => (
                        <div key={index}>
                          {item.type == "avs_tags" && (
                            <div
                              key={`tags-${index}`}
                              className={ExploreAppsStyles.searchResultItem}
                              onClick={() => handleFastSearchClick(item.tag)}
                            >
                              <div
                                className={
                                  ExploreAppsStyles.searchResultItemIcon
                                }
                              >
                                <img
                                  src="/img/blackIcon/SparkRight.svg"
                                  alt={effectiveSearchValue}
                                  className={
                                    ExploreAppsStyles.searchResultItemImg
                                  }
                                />
                              </div>
                              <div
                                className={ExploreAppsStyles.resultContainer}
                              >
                                <div
                                  title={item.tag}
                                  className={ExploreAppsStyles.resultName}
                                >
                                  {item.tag}
                                </div>
                                <div
                                  className={ExploreAppsStyles.resultCategory}
                                >
                                  AI Search
                                </div>
                              </div>
                            </div>
                          )}

                          {item.type == "apps" && (
                            <div
                              key={`apps-${index}`}
                              className={ExploreAppsStyles.searchResultItem}
                              onClick={() =>
                                handleOpenVideoPage(
                                  0,
                                  item.slug,
                                  "Home Page screens",
                                  1,
                                  item.id,
                                )
                              }
                            >
                              <img
                                src={item.icon}
                                alt={item.name}
                                className={ExploreAppsStyles.resultIcon}
                              />
                              <div
                                className={ExploreAppsStyles.resultContainer}
                              >
                                <h5
                                  title={item.name}
                                  className={ExploreAppsStyles.resultName}
                                >
                                  {item.name}
                                </h5>
                                <h6
                                  className={ExploreAppsStyles.resultCategory}
                                >
                                  {item.shortname}
                                </h6>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                      <div
                        ref={anythingSearchRef}
                        style={{ minHeight: "10px" }}
                      ></div>
                    </>
                  )}

                  {effectiveSearchValue.length < 1 &&
                    (windowWidth < 640 ? (
                      <div className={ExploreAppsStyles.mobileContainerSearch}>
                        {buttonSearchData.map((item, index) => {
                          if (index === 1 || index === 2) {
                            return null;
                          }
                          return (
                            <div
                              key={index}
                              className={ExploreAppsStyles.mobileSearchSection}
                            >
                              <div
                                className={
                                  ExploreAppsStyles.mobileSearchSectionTitle
                                }
                              >
                                {typeof item.icon === "string" ? (
                                  <img src={item.icon} alt={item.name} />
                                ) : (
                                  item.icon
                                )}
                                <h3>{item.name}</h3>
                              </div>
                              {index === 0 && (
                                <>
                                  <div
                                    className={
                                      ExploreAppsStyles.mobileSearchSectionItemTags
                                    }
                                  >
                                    {notLoginTags
                                      .slice(
                                        0,
                                        showMoreStates.trending
                                          ? notLoginTags.length
                                          : MAX_TAGS_TO_SHOW,
                                      )
                                      .map((text: string, tagIndex: number) => (
                                        <div
                                          key={tagIndex}
                                          className={
                                            ExploreAppsStyles.mobileSearchSectionItemTag
                                          }
                                        >
                                          <p
                                            onClick={() =>
                                              me?.is_pro ||
                                              currentOrganization?.is_pro
                                                ? handleFastSearchClick(text)
                                                : handleFastSearchClickNoLogin(
                                                    text,
                                                  )
                                            }
                                          >
                                            {text}
                                          </p>
                                        </div>
                                      ))}
                                  </div>
                                  {notLoginTags.length > MAX_TAGS_TO_SHOW && (
                                    <button
                                      className={
                                        ExploreAppsStyles.showMoreButton
                                      }
                                      onClick={() => toggleShowMore("trending")}
                                    >
                                      {showMoreStates.trending
                                        ? "Show Less"
                                        : "Show More"}
                                      <ChevronDownIcon
                                        size={16}
                                        className={
                                          ExploreAppsStyles.showMoreButtonIcon
                                        }
                                        style={{
                                          transform: showMoreStates.trending
                                            ? "rotate(180deg)"
                                            : "rotate(0deg)",
                                        }}
                                      />
                                    </button>
                                  )}
                                </>
                              )}
                              {index === 3 && (
                                <>
                                  <div
                                    className={
                                      ExploreAppsStyles.mobileSearchSectionItemTags
                                    }
                                  >
                                    {screenTypes
                                      .slice(
                                        0,
                                        showMoreStates.screenTypes
                                          ? screenTypes.length
                                          : MAX_TAGS_TO_SHOW,
                                      )
                                      .map(
                                        (text: string, screenIndex: number) => (
                                          <div
                                            key={screenIndex}
                                            className={
                                              ExploreAppsStyles.mobileSearchSectionItemTag
                                            }
                                          >
                                            <p
                                              onClick={() =>
                                                me?.is_pro ||
                                                currentOrganization?.is_pro
                                                  ? handleFastSearchClick(text)
                                                  : handleFastSearchClickNoLogin(
                                                      text,
                                                    )
                                              }
                                            >
                                              {text}
                                            </p>
                                          </div>
                                        ),
                                      )}
                                  </div>
                                  {screenTypes.length > MAX_TAGS_TO_SHOW && (
                                    <button
                                      className={
                                        ExploreAppsStyles.showMoreButton
                                      }
                                      onClick={() =>
                                        toggleShowMore("screenTypes")
                                      }
                                    >
                                      {showMoreStates.screenTypes
                                        ? "Show Less"
                                        : "Show More"}
                                      <ChevronDownIcon
                                        size={16}
                                        className={
                                          ExploreAppsStyles.showMoreButtonIcon
                                        }
                                        style={{
                                          transform: showMoreStates.screenTypes
                                            ? "rotate(180deg)"
                                            : "rotate(0deg)",
                                        }}
                                      />
                                    </button>
                                  )}
                                </>
                              )}
                              {index === 4 && (
                                <>
                                  <div
                                    className={
                                      ExploreAppsStyles.mobileSearchSectionItemTags
                                    }
                                  >
                                    {uielements
                                      .slice(
                                        0,
                                        showMoreStates.uiElements
                                          ? uielements.length
                                          : MAX_TAGS_TO_SHOW,
                                      )
                                      .map(
                                        (
                                          text: string,
                                          elementIndex: number,
                                        ) => (
                                          <div
                                            key={elementIndex}
                                            className={
                                              ExploreAppsStyles.mobileSearchSectionItemTag
                                            }
                                          >
                                            <p
                                              onClick={() =>
                                                me?.is_pro ||
                                                currentOrganization?.is_pro
                                                  ? handleFastSearchClick(text)
                                                  : handleFastSearchClickNoLogin(
                                                      text,
                                                    )
                                              }
                                            >
                                              {text}
                                            </p>
                                          </div>
                                        ),
                                      )}
                                  </div>
                                  {uielements.length > MAX_TAGS_TO_SHOW && (
                                    <button
                                      className={
                                        ExploreAppsStyles.showMoreButton
                                      }
                                      onClick={() =>
                                        toggleShowMore("uiElements")
                                      }
                                    >
                                      {showMoreStates.uiElements
                                        ? "Show Less"
                                        : "Show More"}
                                      <ChevronDownIcon
                                        size={16}
                                        className={
                                          ExploreAppsStyles.showMoreButtonIcon
                                        }
                                        style={{
                                          transform: showMoreStates.uiElements
                                            ? "rotate(180deg)"
                                            : "rotate(0deg)",
                                        }}
                                      />
                                    </button>
                                  )}
                                </>
                              )}
                              {index === 5 && (
                                <ImageSearchModalContent
                                  onUploadEnd={() =>
                                    dispatch(
                                      filterActions.deactivateFilterMagic(),
                                    )
                                  }
                                  isMobile
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className={ExploreAppsStyles.containerSearch}>
                        <div
                          className={ExploreAppsStyles.containerButtonSearch}
                        >
                          {buttonSearchData.map((item, index) => {
                            if (item.name !== "Developers") {
                              return (
                                <div
                                  key={index}
                                  className={`${ExploreAppsStyles.buttonSearch} ${activeSearchIndex === index ? ExploreAppsStyles.buttonSearchActive : ""}`}
                                  onClick={() => handleSearchButtonClick(index)}
                                >
                                  {typeof item.icon === "string" ? (
                                    <img src={item.icon} alt={item.name} />
                                  ) : (
                                    item.icon
                                  )}
                                  <h5
                                    title={item.name}
                                    className={
                                      ExploreAppsStyles.buttonSearchName
                                    }
                                  >
                                    {item.name}
                                  </h5>
                                </div>
                              );
                            }
                          })}
                        </div>
                        <div
                          className={ExploreAppsStyles.containerSearchContent}
                        >
                          {shouldShowSpinner &&
                          (activeSearchIndex === 1 ||
                            activeSearchIndex === 2) ? (
                            <div className={ExploreAppsStyles.spinner}></div>
                          ) : (
                            <>
                              {activeSearchIndex === 1 &&
                                (anythingSearchResult.length > 0
                                  ? anythingSearchResult
                                  : []
                                )?.map((item: any, index: number) => (
                                  <div
                                    key={index}
                                    className={
                                      ExploreAppsStyles.searchResultItem
                                    }
                                    onClick={() =>
                                      handleOpenVideoPage(
                                        0,
                                        item.slug,
                                        "Home Page screens",
                                        1,
                                        item.id,
                                      )
                                    }
                                  >
                                    <img
                                      src={item.icon}
                                      alt={item.name}
                                      className={ExploreAppsStyles.resultIcon}
                                    />
                                    <div
                                      className={
                                        ExploreAppsStyles.resultContainer
                                      }
                                    >
                                      <p
                                        title={item.name}
                                        className={`${ExploreAppsStyles.resultName} ${ExploreAppsStyles.appName}`}
                                      >
                                        {item.name}
                                      </p>
                                      <p
                                        className={`${ExploreAppsStyles.appShortName}`}
                                      >
                                        {item.shortname}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                            </>
                          )}

                          {activeSearchIndex === 0 && (
                            <div
                              className={`${ExploreAppsStyles.containerMagicMenu} ${!me ? ExploreAppsStyles.containerMagicMenuNotLogin : ""}`}
                            >
                              <div
                                className={
                                  ExploreAppsStyles.containerMagicMenuFast
                                }
                              >
                                {notLoginTags.map(
                                  (text: string, index: number) => (
                                    <p
                                      key={index}
                                      onClick={() =>
                                        me?.is_pro ||
                                        currentOrganization?.is_pro
                                          ? handleFastSearchClick(text)
                                          : handleFastSearchClickNoLogin(text)
                                      }
                                    >
                                      {text}
                                    </p>
                                  ),
                                )}
                              </div>
                            </div>
                          )}

                          {activeSearchIndex === 3 && (
                            <div
                              className={`${ExploreAppsStyles.containerMagicMenu} ${!me ? ExploreAppsStyles.containerMagicMenuNotLogin : ""}`}
                            >
                              <div
                                className={
                                  ExploreAppsStyles.containerMagicMenuFast
                                }
                              >
                                {screenTypes.map(
                                  (text: string, index: number) => (
                                    <p
                                      key={index}
                                      onClick={() =>
                                        me?.is_pro ||
                                        currentOrganization?.is_pro
                                          ? handleFastSearchClick(text)
                                          : handleFastSearchClickNoLogin(text)
                                      }
                                    >
                                      {text}
                                    </p>
                                  ),
                                )}
                              </div>
                            </div>
                          )}

                          {activeSearchIndex === 4 && (
                            <div
                              className={`${ExploreAppsStyles.containerMagicMenu} ${!me ? ExploreAppsStyles.containerMagicMenuNotLogin : ""}`}
                            >
                              <div
                                className={
                                  ExploreAppsStyles.containerMagicMenuFast
                                }
                              >
                                {uielements.map(
                                  (text: string, index: number) => (
                                    <p
                                      key={index}
                                      onClick={() =>
                                        me?.is_pro ||
                                        currentOrganization?.is_pro
                                          ? handleFastSearchClick(text)
                                          : handleFastSearchClickNoLogin(text)
                                      }
                                    >
                                      {text}
                                    </p>
                                  ),
                                )}
                              </div>
                            </div>
                          )}

                          {activeSearchIndex === 5 && (
                            <div style={{ padding: "12px", height: "98%" }}>
                              <ImageSearchModalContent
                                onUploadEnd={() =>
                                  dispatch(
                                    filterActions.deactivateFilterMagic(),
                                  )
                                }
                              />
                            </div>
                          )}

                          <div
                            ref={anythingSearchRef}
                            style={{ width: "100%", minHeight: "10px" }}
                          ></div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </>
  );
};

export default SearchModal;
